import { PaletteOptions } from '@mui/material/styles';

declare module '@mui/material/styles' {
  // adds background and border properties to all of the colour variants at creation time
  interface SimplePaletteColorOptions {
    background?: React.CSSProperties['color'];
    border?: React.CSSProperties['color'];
    darkText?: React.CSSProperties['color'];
  }

  // adds background and border properties to all of the colour variants at use time
  interface PaletteColor {
    background?: React.CSSProperties['color'];
    border?: React.CSSProperties['color'];
    darkText?: React.CSSProperties['color'];
  }
}

interface BetterhelpPalette {
  main: React.CSSProperties['color'];
  dark: React.CSSProperties['color'];
  background: React.CSSProperties['color'];
}


interface OrangePalette {
  main: React.CSSProperties['color'];
  dark: React.CSSProperties['color'];
  background: React.CSSProperties['color'];
}

interface BluePalette {
  main: React.CSSProperties['color'];
  dark: React.CSSProperties['color'];
  background: React.CSSProperties['color'];
}

interface LightBluePalette {
  main: React.CSSProperties['color'];
  dark: React.CSSProperties['color'];
  background: React.CSSProperties['color'];
}
interface YellowPalette {
  main: React.CSSProperties['color'];
  dark: React.CSSProperties['color'];
  background: React.CSSProperties['color'];
}

interface PurpleGrayPalette {
  main: React.CSSProperties['color'];
  background: React.CSSProperties['color'];
}

declare module '@mui/material/styles' {

  /*
    This interface defines the type when using `theme.palette`
  */
  interface Palette {
    lightGold: Palette['primary'];
    legacyPrimary: Palette['primary'];
    primaryBackground: Palette['primary'];
    darkSlate: Palette['primary'];
    orange: OrangePalette;
    blue: BluePalette;
    lightBlue: LightBluePalette;
    yellow: YellowPalette;
    purpleGray: PurpleGrayPalette;
    betterhelp: BetterhelpPalette;
  }

  interface TypeText {
    hint?: string;
  }

  /*
    This interface defines what is passed into `createTheme()`
    tertiary is marked as optional so we don't need to update older themes still floating around
    As this is just for the purposes of creation, we'll always have access to the colors

    See libs/services/src/lib/global-theme/use-theme-styles.hook.ts for an older theme
  */
  interface PaletteOptions {
    type: 'light' | 'dark';
    legacyPrimary: PaletteOptions['primary'];
    lightGold: PaletteOptions['primary'];
    primaryBackground: PaletteOptions['primary'];
    darkSlate: PaletteOptions['primary'];
    orange?: OrangePalette;
    blue?: BluePalette;
    lightBlue?: LightBluePalette;
    yellow?: YellowPalette;
    purpleGray?: PurpleGrayPalette;
    betterhelp?: BetterhelpPalette;
    // hint?: PaletteOptions['text'];
  }
}

export const palette: PaletteOptions = {
  type: 'light',
  primary: {
    main: '#8750D7',
    light: '#BB7FFF',
    dark: '#381866',
    contrastText: '#FFF',
    background: '#F7F2FF',
    border: '#C3A7EB',
  },
  legacyPrimary: {
    main: '#398383',
    light: '#79A8A5',
    dark: '#094040',
    contrastText: '#FFFFFF',
    background: '#DAF1F1',
    border: '#A3BABA',
  },
  secondary: {
    main: '#8750D7',
    light: '#BB7FFF',
    dark: '#381866',
    contrastText: '#FFF',
    background: '#F7F2FF',
    border: '#C3A7EB',
  },
  orange: {
    main: '#E28025',
    dark: '#6D3806',
    background: '#FFF4EA',
  },
  blue: {
    main: '#1C3FB0',
    dark: '#011556',
    background: '#EDF0FB'
  },
  lightBlue: {
    main: '#9DD5D8',
    dark: '#0E6367',
    background: '#E8FEFF'
  },
  yellow: {
    main: '#F2F957',
    dark: '#909700',
    background: '#FDFFCE',
  },
  purpleGray: {
    main: '#F1EEF4',
    background: '#F9F8FB',
  },
  info: {
    main: '#0C7EA3',
    light: '#55ADD4',
    dark: '#005274',
    contrastText: '#FFF',
    darkText: '#053241',
    background: '#E7F2F6',
    border: '#0C7EA3',
  },
  success: {
    main: '#6BC9A0',
    light: '#9DFCD1',
    dark: '#31835F',
    contrastText: '#212121',
    darkText: '#2D5543',
    background: '#F0FAF5',
    border: '#B5E4CF',
  },
  warning: {
    main: '#E37B3C',
    light: '#F2A964',
    dark: '#B34D1E',
    contrastText: '#212121',
    darkText: '#5B3118',
    background: '#FCF2EB',
    border: '#F1BD9D',
  },
  error: {
    main: '#BE331C',
    light: '#EC6742',
    dark: '#892411',
    contrastText: '#FFF',
    darkText: '#4C140B',
    background: '#F8EBE8',
    border: '#DF998D',
  },
  lightGold: {
    main: '#DAD7CC',
    contrastText: '#D5D2C7',
  },
  primaryBackground: {
    main: 'rgba(72, 117, 117, 0.08)'
  },
  text: {
    primary: '#212121',
    secondary: '#757575',
    disabled: '#9E9E9E',
    hint: '#9E9E9E'
  },
  darkSlate: {
    main: '#434C55',
  },
  background: {
    paper: '#FFF',
  },
  grey: {
    A400: '#616161',
    A700: '#303030',
  },
  betterhelp: {
    main: '#7EC16A',
    dark: '#325343',
    background: '#DEF6D9',
  }
};
